import { useEffect, useState } from "react";
import type {
  CtaListData,
  CtaPlatform,
  GoodsData,
  GoodsImages,
  NewsListData,
  NewsListItem,
} from "@laftel-ip-originals/notion/contracts";
import { getOriginalBySlug } from "~/data/ip";
import { getCtaListPath, getGoodsPath, getNewsListPath } from "~/data/content-paths";
import { fetchStaticJson, getCachedJson, preloadJson } from "~/lib/json-cache";

interface IpContentListState {
  ctaCategories: Record<string, CtaPlatform[]>;
  ctaIsLoading: boolean;
  error?: string;
  goodsCoverImage?: GoodsImages;
  hasGoods: boolean;
  isLoading: boolean;
  newsItems: NewsListItem[];
}

export function useIpContentList(slug: string | undefined): IpContentListState {
  const original = slug ? getOriginalBySlug(slug) : undefined;
  const newsPath = original ? getNewsListPath(slug!) : null;
  const goodsPath = original ? getGoodsPath(slug!) : null;
  const ctaPath = original ? getCtaListPath(slug!) : null;

  const [state, setState] = useState<IpContentListState>(() => {
    if (!newsPath || !goodsPath) {
      return {
        isLoading: false,
        ctaIsLoading: false,
        hasGoods: false,
        newsItems: [],
        ctaCategories: {},
      };
    }

    preloadJson(newsPath);
    preloadJson(goodsPath);
    if (ctaPath) preloadJson(ctaPath);

    const cachedNews = getCachedJson<NewsListData>(newsPath);
    const cachedGoods = getCachedJson<GoodsData>(goodsPath);
    const cachedCta = ctaPath ? getCachedJson<CtaListData>(ctaPath) : null;

    return {
      isLoading: !cachedNews,
      ctaIsLoading: !!ctaPath && !cachedCta,
      newsItems: cachedNews?.items ?? [],
      hasGoods: (cachedGoods?.stores.length ?? 0) > 0,
      goodsCoverImage: cachedGoods?.coverImage,
      ctaCategories: cachedCta?.categories ?? {},
    };
  });

  useEffect(() => {
    if (!newsPath || !goodsPath) {
      setState({
        isLoading: false,
        ctaIsLoading: false,
        hasGoods: false,
        newsItems: [],
        ctaCategories: {},
      });
      return;
    }

    const cachedNews = getCachedJson<NewsListData>(newsPath);
    const cachedGoods = getCachedJson<GoodsData>(goodsPath);
    const cachedCta = ctaPath ? getCachedJson<CtaListData>(ctaPath) : null;

    if (cachedNews) {
      setState((prev) => ({
        isLoading: false,
        ctaIsLoading: !!ctaPath && !cachedCta,
        newsItems: cachedNews.items,
        hasGoods: (cachedGoods?.stores.length ?? 0) > 0,
        goodsCoverImage: cachedGoods?.coverImage,
        ctaCategories: cachedCta?.categories ?? prev.ctaCategories,
      }));
      if (!ctaPath || cachedCta) return;
    }

    let cancelled = false;

    setState((prev) => ({
      isLoading: !cachedNews,
      ctaIsLoading: !!ctaPath && !cachedCta,
      newsItems: cachedNews?.items ?? [],
      hasGoods: (cachedGoods?.stores.length ?? 0) > 0,
      goodsCoverImage: cachedGoods?.coverImage,
      ctaCategories: cachedCta?.categories ?? prev.ctaCategories,
    }));

    Promise.allSettled([
      fetchStaticJson<NewsListData>(newsPath),
      fetchStaticJson<GoodsData>(goodsPath),
    ]).then(([newsResult, goodsResult]) => {
      if (cancelled) return;
      const newsData = newsResult.status === "fulfilled" ? newsResult.value : null;
      const goodsData = goodsResult.status === "fulfilled" ? goodsResult.value : null;

      setState((prev) => ({
        ...prev,
        isLoading: false,
        newsItems: newsData?.items ?? [],
        hasGoods: (goodsData?.stores.length ?? 0) > 0,
        goodsCoverImage: goodsData?.coverImage,
        ...(newsResult.status === "rejected"
          ? {
              error:
                newsResult.reason instanceof Error
                  ? newsResult.reason.message
                  : "Failed to load content.",
            }
          : {}),
      }));
    });

    // cta: 별도 fetch, 실패 시 캐시 보존
    if (ctaPath && !cachedCta) {
      fetchStaticJson<CtaListData>(ctaPath)
        .then((ctaData) => {
          if (!cancelled) {
            setState((prev) => ({
              ...prev,
              ctaIsLoading: false,
              ctaCategories: ctaData.categories,
            }));
          }
        })
        .catch(() => {
          if (!cancelled) {
            setState((prev) => ({
              ...prev,
              ctaIsLoading: false,
            }));
          }
        });
    }

    return () => {
      cancelled = true;
    };
  }, [newsPath, goodsPath, ctaPath]);

  return state;
}
